import { AxiosResponse } from "axios";
import { ButtonText } from "components/Atoms/Button";
import { SearchInput } from "components/Atoms/SearchInput";
import CultureFlag from "components/Culture/CultureFlag";
import { LinkedPanelMessage } from "components/linkedBookkeepings/LinkedPanelMessage";
import PanelSegment from "components/panels/PanelSegment/PanelSegment";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import { LinkedWordTemplatePanelInstructionBase } from "components/Picker/WordTemplatePicker/model";
import Table from "components/Tables/Table";
import React, { useState } from "react";
import { useCustomQuery, useManualPagination } from "utilities/hookutils";
import { routes } from "utilities/routing/generatedRoutes";
import { useDebounce } from "utilities/usehooks";

interface Props<T extends LinkedWordTemplatePanelInstructionBase> {
  queryKey: string;
  perPage: number;
  title: string;
  noSelectionLabel: string;
  listFunc: (instruction: T) => Promise<AxiosResponse<server.dto.ApiResponseWrapperWithRequest<server.dto.WordTemplate[], server.dto.ListLinkedWordTemplateFilters>, any>>;
  initialInstruction: T;
  onDelete: (wordTemplate: server.dto.WordTemplate) => void;
  onRemoveAll?: () => Promise<AxiosResponse>;
  onAddAll?: () => Promise<AxiosResponse>;
  onAdd: () => void;
}

export const LinkedWordTemplatesPanel = <T extends LinkedWordTemplatePanelInstructionBase>(props: Props<T>) => {
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const [total, setTotal] = React.useState<number>(0);
  const [selected, setSelected] = React.useState<number>(0);
  const [isRemovingAll, setIsRemovingAll] = useState<boolean>(false);
  const debouncedSearchQuery = useDebounce(searchQuery);

  const [instruction, setInstruction] = useState<T>({
    page: 1,
    perPage: props.perPage,
    ...props.initialInstruction,
  });

  React.useEffect(() => {
    setInstruction({
      ...instruction,
      keyword: debouncedSearchQuery,
      page: 1,
    });
  }, [debouncedSearchQuery]);

  const api = useCustomQuery({
    queryFn: () => props.listFunc(instruction),
    queryKey: ["wordTemplate", instruction, props.queryKey],
    onSuccess: (response) => {
      setTotal(response.data.absoluteCount);
      setSelected(response.data.selectedCount);
      pagination.setPagination(response.data.pagination);
    }
  });

  const onRemoveAll = async () => {
    setIsRemovingAll(true);
    await props.onRemoveAll?.();
    setIsRemovingAll(false);
    api.refetch();
  }

  const onAddAll = async () => {
    await props.onAddAll?.();
    api.refetch();
  }

  const paginate = (paginationData: Partial<server.dto.PaginationData>) => {
    setInstruction({
      ...instruction,
      keyword: debouncedSearchQuery,
      page: paginationData.page,
      perPage: paginationData.perPage
    });
  };

  const pagination = useManualPagination(instruction.page || 1, props.perPage, paginate);
  const items = api.data?.data?.data || [];
  const displaySearchBar = selected > props.perPage;
  const displayRemoveAll = selected > 4 && props.onRemoveAll;
  const isLoading = api.isLoading;

  return <SectionPanel.Panel
    header={<>
      <PanelSegment.Title title={props.title} />
      {(selected < total && total > 0) && <PanelSegment.Button
        label={RESX.Buttons.Add}
        type="secondary"
        onClick={props.onAdd}
      />}
    </>}
  >
    <div className="flex-column gap">
      {!isLoading && <LinkedPanelMessage
        selected={selected}
        total={total}
        nothingSelectedMessage={props.noSelectionLabel}
        entityName={RESX.WordTemplate.WordTemplates}
        onAddAll={onAddAll}
      />}
      {displaySearchBar && <div>
        <SearchInput
          onChange={(_, { value }) => { setSearchQuery(value); }}
          value={searchQuery}
          placeholder={RESX.Company.SearchByName}
        />
      </div>}
      {pagination && <div className="flex justify-end">
        <PanelSegment.Pagination>{pagination.Component}</PanelSegment.Pagination>
      </div>}
      <Table
        variant="cards"
        hideNoDataAvailableDiv
        canDelete={() => true}
        onDelete={props.onDelete}
        items={items}
        isLoading={isLoading}
        getKey={wordTemplate => wordTemplate.wordTemplateId.toString()}
        hideHeader
        columns={[{
          key: "flag",
          header: "",
          size: "52px",
          render: item => <CultureFlag lcid={item.lcid} />
        }, {
          key: "name",
          header: "",
          render: (wordTemplate) => <a href={routes.wordTemplate_Detail(wordTemplate.wordTemplateId).absolute}>{wordTemplate.title}</a>,
          size: "1fr"
        }]}
      />
    </div>
    {displayRemoveAll && <div className="flex justify-end margin-top">
      <ButtonText
        className="no-padding"
        label={RESX.Buttons.RemoveAll}
        onClick={onRemoveAll}
        loading={isRemovingAll}
      />
    </div>}
  </SectionPanel.Panel>
}
